import { useState, type FormEvent } from 'react';
import { REGIONS, type MeResponse, type Region } from '@photos/shared';
import { useProfileOptions, useRequestAccess } from '../api/hooks';
import { Brand } from '../components/Header';
import { Icon } from '../components/Icon';
import { ChurchOptions, ServiceCheckboxes } from '../components/GroupPickers';
import { PhoneField, type PhoneValue } from '../components/PhoneField';
import { UserMenu } from '../components/UserMenu';

/** For signed-in people without an active account: request access, or see that the request is pending. */
export function RequestAccessPage({ me }: { me: MeResponse }) {
  const user = me.user;
  // A JBCH USA profile from another app pre-fills the request: then only the reason (and anything missing) is asked.
  const profile = me.profile;
  const ask = { name: !profile?.name, church: !profile?.church, region: !profile?.region, details: !profile };
  const [name, setName] = useState(profile?.name ?? '');
  const [church, setChurch] = useState(profile?.church ?? '');
  const [region, setRegion] = useState<Region>(profile?.region ?? 'East');
  const [fellowship, setFellowship] = useState<string | null>(profile?.fellowship ?? null);
  const [services, setServices] = useState<string[]>(profile?.services ?? []);
  const [reason, setReason] = useState('');
  const [phone, setPhone] = useState<PhoneValue>({ e164: profile?.phone ?? null, valid: true });
  const req = useRequestAccess();
  const options = useProfileOptions();
  const churches = options.data?.churches ?? [];
  const churchLabel =
    churches.find((c) => c.name === (user?.church ?? profile?.church))?.label ?? user?.church ?? profile?.church;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    req.mutate({
      ...(ask.name ? { name: name.trim() } : {}),
      ...(ask.church ? { church } : {}),
      ...(ask.region ? { region } : {}),
      ...(ask.details ? { fellowship, services, phone: phone.e164 } : {}),
      reason: reason.trim(),
    });
  };

  return (
    <>
      <header className="top">
        <div className="top-row studio-row solo">
          <div className="top-left">
            <Brand />
          </div>
          <div className="actions">
            <UserMenu
              name={user?.name ?? profile?.name ?? 'Guest'}
              subtitle={user?.email ?? profile?.email ?? 'Not a member yet'}
            />
          </div>
        </div>
      </header>
      <main className="wrap narrow-page">
        {user?.status === 'pending' ? (
          <div className="panel">
            <div className="notice" style={{ marginBottom: 12 }}>
              <Icon name="clock" />
              <span>Your request is waiting for approval.</span>
            </div>
            <h3>Thanks, {user.name}</h3>
            <p className="help" style={{ margin: 0 }}>
              An admin will review your request for {churchLabel || 'your church'}. You’ll be able to see photos as soon as it’s
              approved — just come back to this page.
            </p>
          </div>
        ) : user?.status === 'suspended' ? (
          <div className="panel">
            <h3>Your access is paused</h3>
            <p className="help" style={{ margin: 0 }}>
              Please contact your church’s media team if you think this is a mistake.
            </p>
          </div>
        ) : (
          <form className="panel" onSubmit={submit}>
            <h3>Request access to JBCH USA Photos</h3>
            <p className="help">
              {profile
                ? 'The photo archive is private to church members. An admin will approve your access.'
                : 'The photo archive is private to church members. Tell us who you are and an admin will approve your access.'}
            </p>
            {profile && (
              <p className="help">
                Using your JBCH USA profile: <b>{profile.name}</b>
                {profile.church && <> · {churchLabel}</>}
                {profile.region && <> · {profile.region}</>}. You can change it in My profile.
              </p>
            )}
            {ask.name && (
              <div className="field">
                <label htmlFor="ra-name">Your name</label>
                <input
                  id="ra-name"
                  className="inp"
                  required
                  maxLength={80}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            )}
            {ask.church && (
              <div className="field">
                <label htmlFor="ra-church">Church</label>
                <select id="ra-church" className="inp" required value={church} onChange={(e) => setChurch(e.target.value)}>
                  <option value="" disabled>
                    {options.isPending ? 'Loading churches…' : 'Choose your church'}
                  </option>
                  <ChurchOptions churches={churches} />
                </select>
              </div>
            )}
            {ask.region && (
              <div className="field">
                <label htmlFor="ra-region">Region</label>
                <select id="ra-region" className="inp" value={region} onChange={(e) => setRegion(e.target.value as Region)}>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {ask.details && (options.data?.fellowships.length ?? 0) > 0 && (
              <div className="field">
                <label htmlFor="ra-fellowship">
                  Fellowship group <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</small>
                </label>
                <select
                  id="ra-fellowship"
                  className="inp"
                  value={fellowship ?? ''}
                  onChange={(e) => setFellowship(e.target.value || null)}
                >
                  <option value="">None</option>
                  {options.data!.fellowships.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {ask.details && (options.data?.services.length ?? 0) > 0 && (
              <div className="field">
                <label id="ra-services">
                  Service groups <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional, any number)</small>
                </label>
                <ServiceCheckboxes
                  options={options.data!.services}
                  value={services}
                  onChange={setServices}
                  label="Service groups"
                />
              </div>
            )}
            {ask.details && (
              <div className="field">
                <label htmlFor="ra-phone">
                  Phone <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</small>
                </label>
                <PhoneField id="ra-phone" initial={null} onChange={setPhone} />
              </div>
            )}
            <div className="field">
              <label htmlFor="ra-reason">Why do you need access?</label>
              <input
                id="ra-reason"
                className="inp"
                maxLength={280}
                placeholder="e.g. Church school teacher, media team member"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            {req.error && (
              <p className="help" style={{ color: 'var(--danger)' }}>
                {req.error.message}
              </p>
            )}
            <button
              type="submit"
              className="btn primary"
              style={{ width: '100%', justifyContent: 'center', height: 42 }}
              disabled={req.isPending || !name.trim() || !church || !phone.valid}
            >
              Request access
            </button>
          </form>
        )}
      </main>
    </>
  );
}
